/**
 * Toasts — roadmap 4.2.
 *
 * On Radix's primitive for the parts that are genuinely hard: the toast is
 * announced without stealing focus, F8 moves into the region, and the queue does
 * not trap a keyboard user.
 *
 * Placement is the inline **end**, bottom — so it sits bottom-right in English
 * and bottom-left in Arabic, away from the direction the eye returns to.
 */
import * as ToastPrimitive from '@radix-ui/react-toast'
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

import { cn } from './cn'
import { ToastContext, type ToastApi, type ToastOptions, type ToastTone } from './toastContext'

type Entry = ToastOptions & { id: number }

const TONE_STYLES: Record<ToastTone, string> = {
  neutral: 'border-line',
  success: 'border-success/40',
  danger: 'border-danger/40',
}

const TONE_ICONS: Record<ToastTone, typeof Info> = {
  neutral: Info,
  success: CheckCircle2,
  danger: AlertCircle,
}

const TONE_ICON_STYLES: Record<ToastTone, string> = {
  neutral: 'text-fg-subtle',
  success: 'text-success',
  danger: 'text-danger',
}

export function Toaster({
  children,
  /** Screen-reader name for the region. Passed in so it stays translatable. */
  label,
  closeLabel,
}: {
  children: React.ReactNode
  label: string
  closeLabel: string
}) {
  const [entries, setEntries] = useState<Entry[]>([])

  const toast = useCallback((options: ToastOptions) => {
    // A counter, not `Date.now()`: two toasts raised in the same millisecond
    // would collide and React would reuse one DOM node for both.
    setEntries((current) => [...current, { ...options, id: nextId() }])
  }, [])

  const api = useMemo<ToastApi>(() => ({ toast }), [toast])

  const dismiss = useCallback((id: number) => {
    setEntries((current) => current.filter((entry) => entry.id !== id))
  }, [])

  return (
    <ToastContext value={api}>
      <ToastPrimitive.Provider label={label} swipeDirection="right">
        {children}

        {entries.map((entry) => {
          const tone = entry.tone ?? 'neutral'
          const Icon = TONE_ICONS[tone]

          return (
            <ToastPrimitive.Root
              key={entry.id}
              duration={entry.duration ?? 5000}
              onOpenChange={(open) => {
                if (!open) dismiss(entry.id)
              }}
              className={cn(
                'flex items-start gap-3 rounded-xl border bg-raised p-4 shadow-lg ring-1 ring-edge',
                'data-[state=closed]:animate-out data-[state=closed]:fade-out',
                TONE_STYLES[tone],
              )}
            >
              <Icon
                aria-hidden="true"
                className={cn('mt-0.5 size-5 shrink-0', TONE_ICON_STYLES[tone])}
              />
              <div className="min-w-0 flex-1">
                <ToastPrimitive.Title className="text-sm font-semibold">
                  {entry.title}
                </ToastPrimitive.Title>
                {entry.description ? (
                  <ToastPrimitive.Description className="mt-0.5 text-sm text-fg-muted">
                    {entry.description}
                  </ToastPrimitive.Description>
                ) : null}
              </div>
              <ToastPrimitive.Close
                aria-label={closeLabel}
                className="rounded-md p-1 text-fg-subtle hover:bg-sunken hover:text-fg"
              >
                <X aria-hidden="true" className="size-4" />
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          )
        })}

        <ToastPrimitive.Viewport className="fixed bottom-0 end-0 z-100 flex w-full max-w-sm flex-col gap-2 p-4" />
      </ToastPrimitive.Provider>
    </ToastContext>
  )
}

let counter = 0
function nextId(): number {
  counter += 1
  return counter
}
